/**
 * Hooks para subir y quitar el icono de una app desde el admin.
 *
 * PUT /api/admin/apps/{id}/icon (multipart, campo `file`) y
 * DELETE /api/admin/apps/{id}/icon. El cliente `api` añade el CSRF.
 * Al terminar se invalidan el listado admin y el catálogo, que pintan el icono.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api, ApiError } from '@/api/client';
import type { AppAdminEntry } from '@/admin/useAdminApps';

/** Debe coincidir con MAX_ICON_BYTES del backend (app/apps/icons.py). */
export const MAX_ICON_BYTES = 256 * 1024;
export const ICON_ACCEPT = 'image/png,image/jpeg,image/webp';
const ACCEPTED_TYPES = new Set(ICON_ACCEPT.split(','));

export type IconFileProblem = 'empty' | 'tooLarge' | 'badType';

/**
 * Validación previa en cliente (para dar un mensaje claro sin subir nada).
 * El backend vuelve a validar por magic bytes: el `type` del navegador no es fiable.
 */
export function checkIconFile(file: File): IconFileProblem | null {
  if (file.size === 0) return 'empty';
  if (file.size > MAX_ICON_BYTES) return 'tooLarge';
  if (!ACCEPTED_TYPES.has(file.type)) return 'badType';
  return null;
}

function useInvalidateApps() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'apps'] });
    void qc.invalidateQueries({ queryKey: ['apps'] });
  };
}

export function useUploadAppIcon() {
  const invalidate = useInvalidateApps();
  return useMutation<AppAdminEntry, ApiError, { id: number; file: File }>({
    mutationFn: ({ id, file }) => {
      const form = new FormData();
      form.append('file', file);
      return api<AppAdminEntry>(`/api/admin/apps/${id}/icon`, { method: 'PUT', body: form });
    },
    onSuccess: invalidate,
  });
}

export function useDeleteAppIcon() {
  const invalidate = useInvalidateApps();
  return useMutation<AppAdminEntry, ApiError, number>({
    mutationFn: (id) => api<AppAdminEntry>(`/api/admin/apps/${id}/icon`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}
